import React, { useRef, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import CruiseWhenSelectContent from './CruiseWhenSelectContent'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import Group from 'components/utils/Group'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import CruiseFilterFooter from '../Common/CruiseFilterFooter'
import useToggle from 'hooks/useToggle'
import type { CRUISE_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import CruiseFilterInput from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseFilterInput'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

import * as Analytics from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'

interface Props {
  inputValue: string
  isChipMode?: boolean
  initialFilters?: App.CruiseInitialFilters
  shouldIgnoreFlashOffers?: boolean
  onChange: (values: App.CruiseGlobalFilters) => void
}

function CruiseWhenSelectMobile({ inputValue, onChange, isChipMode, initialFilters, shouldIgnoreFlashOffers }: Props) {
  const intl = useIntl()
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)
  const [isOpen, toggleModal] = useToggle(false)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const globalSearchState = useContext(GlobalSearchStateContext)
  const {
    flexibleMonths,
    durationMax,
    durationMin,
    durationRange,
    checkinDate,
    checkoutDate,
  } = globalSearchState

  const onClear = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_cleared',
      action: 'cleared',
      category: 'cruise_when_filter',
      type: 'interaction',
    })
    searchDispatch({ type: GlobalSearchStateActions.SET_DURATION_RANGE, min: undefined, max: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE, flexibleMonths: '' })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: undefined })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION_RANGE, durationRange: [] })
  }, [searchDispatch])

  const onChangeWhen = useCallback((values: App.CruiseGlobalFilters) => {
    const { flexibleMonths, durationMax, durationMin, durationRange, checkinDate, checkoutDate } = values
    searchDispatch({ type: GlobalSearchStateActions.SET_DURATION_RANGE, min: durationMin, max: durationMax })
    searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE, flexibleMonths: flexibleMonths! })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: checkinDate })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: checkoutDate })
    if (durationRange) {
      searchDispatch({ type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION_RANGE, durationRange: durationRange as Array<CRUISE_FLEXIBLE_DURATION_RANGE> })
    }
  }, [searchDispatch])

  const handleOnApply = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'filter_applied',
      action: 'applied',
      category: 'cruise_when_filter',
      type: 'interaction',
    })

    const flexibleMonthsLabel = intl.formatMessage({
      id: '0PZ6/nvW5e',
      defaultMessage: 'Flexible Months',
      description: 'Label for flexible months filter context',
    })
    const durationRangeLabel = intl.formatMessage({
      id: 'Ovuz/BSbTi',
      defaultMessage: 'Duration Range',
      description: 'Label for duration range filter context',
    })
    const checkinDateLabel = intl.formatMessage({
      id: 'oduL5VBW75',
      defaultMessage: 'Check-in Date',
      description: 'Label for check-in date filter context',
    })
    const checkoutDateLabel = intl.formatMessage({
      id: 'Dpt2jYWXkn',
      defaultMessage: 'Check-out Date',
      description: 'Label for check-out date filter context',
    })

    const filterContexts: Array<{ label: string; type: string; value: string | number }> = []
    if (flexibleMonths) {
      filterContexts.push({ label: flexibleMonthsLabel, type: 'flexible_months', value: flexibleMonths })
    }
    if (durationRange?.length) {
      filterContexts.push({ label: durationRangeLabel, type: 'duration_range', value: durationRange.join(', ') })
    }
    if (checkinDate) {
      filterContexts.push({ label: checkinDateLabel, type: 'checkin_date', value: checkinDate.format(DMY_CASUAL_FORMAT) })
    }
    if (checkoutDate) {
      filterContexts.push({ label: checkoutDateLabel, type: 'checkout_date', value: checkoutDate.format(DMY_CASUAL_FORMAT) })
    }

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'filters' },
      sortContext: { value: 'recommended' },
      searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
      filterContexts,
    }))

    onChange({
      flexibleMonths,
      durationMax,
      durationMin,
      durationRange,
      checkinDate,
      checkoutDate,
    })
    toggleModal()
  }, [checkinDate, checkoutDate, durationMax, durationMin, durationRange, flexibleMonths, onChange, toggleModal, globalSearchState, intl])

  const whenLabel = intl.formatMessage({
    id: '5HLUXUZUEf',
    defaultMessage: 'When',
    description: 'Label for when filter input',
  })

  const anytimePlaceholder = intl.formatMessage({
    id: 'tET+l7wQBc',
    defaultMessage: 'Anytime',
    description: 'Placeholder text for when filter input',
  })

  return <>
    <CruiseFilterInput
      isOpen={isOpen}
      data-testid="cruise-when-select-mobile-input"
      label={whenLabel}
      placeholder={anytimePlaceholder}
      inputValue={inputValue}
      formFieldRef={dropdownTriggerRef}
      onClick={() => {
        Analytics.trackClientEvent({
          subject: 'filter_opened',
          action: 'opened',
          category: 'cruise_when_filter',
          type: 'interaction',
        })
        toggleModal()
      }}
      isChipMode={isChipMode}
    />

    <ModalBase
      mode="drawer"
      isOpen={isOpen}
      onClose={toggleModal}
    >
      <ModalHeader
        title={whenLabel}
        onCloseButtonClick={toggleModal}
      />
      <ModalBody>
        <Group direction="vertical" gap={24}>
          <CruiseWhenSelectContent onChange={onChangeWhen} initialFilters={initialFilters} drawerMode />
        </Group>
      </ModalBody>
      <ModalFooter>
        <CruiseFilterFooter
          onApply={handleOnApply}
          onClear={onClear}
          initialFilters={initialFilters}
          shouldIgnoreFlashOffers={shouldIgnoreFlashOffers}
        />
      </ModalFooter>
    </ModalBase>
  </>
}

export default CruiseWhenSelectMobile
